import React from 'react';
import { LucideIcon, Lightbulb, AlertTriangle } from 'lucide-react';
import type { Locale } from '../../types/locale';

interface Step {
  title: string;
  description: string | React.ReactNode;
  tip?: string;
  warning?: string;
  code?: {
    language: string;
    content: string;
  };
  icon?: LucideIcon;
}

interface StepByStepProps {
  // Section ID for jump links
  id?: string;

  // H2 heading
  heading: string;

  // Intro paragraph
  intro?: string;

  // Steps
  steps: Step[];

  // Background color variant
  variant?: 'default' | 'alternate';

  // Locale for translations
  locale?: Locale;
}

/**
 * Localized labels for StepByStep component
 * SEO-optimized with natural phrasing per locale
 */
const translations: Record<Locale, {
  tipLabel: string;
  noteLabel: string;
  stepPrefix: string;
}> = {
  en: {
    tipLabel: 'Tip:',
    noteLabel: 'Note:',
    stepPrefix: 'Step',
  },
  de: {
    tipLabel: 'Tipp:',
    noteLabel: 'Hinweis:',
    stepPrefix: 'Schritt',
  },
  'pt-BR': {
    tipLabel: 'Dica:',
    noteLabel: 'Observacao:',
    stepPrefix: 'Passo',
  },
  'ms-MY': {
    tipLabel: 'Petua:',
    noteLabel: 'Nota:',
    stepPrefix: 'Langkah',
  },
  es: {
    tipLabel: 'Consejo:',
    noteLabel: 'Nota:',
    stepPrefix: 'Paso',
  },
  fr: {
    tipLabel: 'Conseil :',
    noteLabel: 'Remarque :',
    stepPrefix: 'Etape',
  },
  ja: {
    tipLabel: 'ヒント：',
    noteLabel: '注意：',
    stepPrefix: 'ステップ',
  },
  id: {
    tipLabel: 'Tips:',
    noteLabel: 'Catatan:',
    stepPrefix: 'Langkah',
  },
};

/**
 * StepByStep - Numbered instructional steps component
 *
 * SEO Benefits:
 * - Can trigger Google's "How To" rich snippets
 * - Provides structured content for crawlers
 * - Tips and warnings add depth (E-E-A-T signals)
 */
export const StepByStep: React.FC<StepByStepProps> = ({
  id = 'how-to',
  heading,
  intro,
  steps,
  variant = 'default',
  locale = 'en',
}) => {
  const t = translations[locale] || translations.en;
  const bgClass =
    variant === 'alternate'
      ? 'bg-neutral-50 dark:bg-neutral-900/50'
      : 'bg-white dark:bg-neutral-950';

  return (
    <section id={id} className={`py-12 md:py-16 ${bgClass}`}>
      <div className="container mx-auto px-4 max-w-4xl">
        {/* H2 Heading */}
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white mb-4">
          {heading}
        </h2>

        {/* Intro */}
        {intro && (
          <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-8">
            {intro}
          </p>
        )}

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="relative pl-12 md:pl-16 pb-8 border-l-2 border-neutral-200 dark:border-neutral-700 last:border-l-transparent last:pb-0"
              >
                {/* Step Number */}
                <div className="absolute left-0 -translate-x-1/2 w-8 h-8 md:w-10 md:h-10 bg-accent-600 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-base">
                  {index + 1}
                </div>

                {/* Step Content */}
                <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-6 shadow-sm">
                  {/* Step Header */}
                  <div className="flex items-start gap-3 mb-3">
                    {IconComponent && (
                      <IconComponent
                        size={24}
                        className="flex-shrink-0 text-accent-600 dark:text-accent-400 mt-0.5"
                      />
                    )}
                    <h3 className="text-lg md:text-xl font-semibold text-neutral-900 dark:text-white">
                      {step.title}
                    </h3>
                  </div>

                  {/* Step Description */}
                  <div className="text-neutral-700 dark:text-neutral-300 mb-4">
                    {typeof step.description === 'string' ? (
                      <p>{step.description}</p>
                    ) : (
                      step.description
                    )}
                  </div>

                  {/* Code Block */}
                  {step.code && (
                    <div className="mb-4">
                      <div className="bg-neutral-900 dark:bg-neutral-800 rounded-lg overflow-hidden">
                        <div className="px-4 py-2 bg-neutral-800 dark:bg-neutral-700 text-neutral-400 text-xs font-mono">
                          {step.code.language}
                        </div>
                        <pre className="p-4 overflow-x-auto">
                          <code className="text-sm text-neutral-100 font-mono">
                            {step.code.content}
                          </code>
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Tip */}
                  {step.tip && (
                    <div className="flex items-start gap-3 p-4 bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg mb-3">
                      <Lightbulb
                        size={20}
                        className="flex-shrink-0 text-accent-600 dark:text-accent-400 mt-0.5"
                      />
                      <p className="text-sm text-accent-800 dark:text-accent-200">
                        <span className="font-semibold">{t.tipLabel} </span>
                        {step.tip}
                      </p>
                    </div>
                  )}

                  {/* Warning */}
                  {step.warning && (
                    <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <AlertTriangle
                        size={20}
                        className="flex-shrink-0 text-yellow-600 dark:text-yellow-400 mt-0.5"
                      />
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        <span className="font-semibold">{t.noteLabel} </span>
                        {step.warning}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StepByStep;
